/**
 * Univer Viewer Component
 * Converts simple 2D array data to Univer format
 */

import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { createUniver, createWorkbookWithData } from '../../lib/univer-setup';
import type { Univer } from '@univerjs/core';

interface SheetData {
  name: string;
  rows: any[][];
  rowCount: number;
  columnCount: number;
  // 🎨 Sheet-level formatting
  colWidths?: number[];
  rowHeights?: number[];
  merges?: Array<{ s: { r: number; c: number }; e: { r: number; c: number } }>;
  defaultColWidth?: number;
  defaultRowHeight?: number;
}

interface UniverViewerProps {
  data: SheetData[];
  onCellClick?: (cell: any) => void;
  onRangeSelect?: (range: any) => void;
  onCellEdit?: (edit: { sheetName: string; sheetIndex: number; row: number; col: number; value: any; formula?: string }) => void;
  onSheetChange?: (sheetInfo: { name: string; index: number }) => void;
  readOnly?: boolean;
  height?: string;
}

export interface UniverViewerHandle {
  applyModifications: (modifications: any[]) => Promise<void>;
  reloadData: (newData: SheetData[]) => Promise<void>;
  activateSheet: (sheetIndex: number) => void;
}

export const UniverViewer = forwardRef<UniverViewerHandle, UniverViewerProps>(({
  data,
  onCellClick,
  onRangeSelect,
  onCellEdit,
  onSheetChange,
  readOnly = false,
  height = '100%'
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const univerRef = useRef<Univer | null>(null);
  const univerAPIRef = useRef<any>(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    applyModifications: async (modifications: any[]) => {
      if (!univerAPIRef.current) {
        console.warn('⚠️ Univer API not ready for incremental updates');
        return;
      }

      console.log('🔄 Applying', modifications.length, 'modifications incrementally');

      for (const mod of modifications) {
        try {
          await applyModification(univerAPIRef.current, mod);
        } catch (error) {
          console.error('❌ Failed to apply modification:', mod, error);
        }
      }

      console.log('✅ All modifications applied');
    },
    reloadData: async (newData: SheetData[]) => {
      // For structural changes (add/delete sheets), do a full reload
      console.log('🔄 Full reload requested');
      
      if (univerRef.current) {
        try {
          univerRef.current.dispose();
        } catch (err) {
          console.error('Error disposing for reload:', err);
        }
        univerRef.current = null;
        univerAPIRef.current = null;
      }

      // Re-initialize with new data
      const univerResult = createUniver(containerRef.current!);
      const univer = typeof univerResult === 'object' && 'univer' in univerResult 
        ? univerResult.univer 
        : univerResult;
      const univerAPI = typeof univerResult === 'object' && 'univerAPI' in univerResult 
        ? univerResult.univerAPI 
        : null;
      
      univerRef.current = univer;
      univerAPIRef.current = univerAPI;

      const workbookData = convertToUniverFormat(newData);
      await createWorkbookWithData(workbookData);
      
      // Force formula recalculation after data load
      if (univerAPI) {
        try {
          console.log('🔄 Triggering formula recalculation...');
          const formulaEngine = univerAPI.getFormula();
          if (formulaEngine) {
            formulaEngine.executeCalculation();
          }
        } catch (err) {
          console.log('ℹ️ Formula recalculation not available:', err);
        }
      }
      
      console.log('✅ Full reload complete');
    },
    activateSheet: (sheetIndex: number) => {
      if (!univerAPIRef.current) {
        console.warn('⚠️ Univer API not ready for sheet activation');
        return;
      }

      try {
        const workbook = univerAPIRef.current.getActiveWorkbook();
        if (!workbook) {
          console.warn('⚠️ No active workbook');
          return;
        }

        const sheets = workbook.getSheets();
        if (sheetIndex >= 0 && sheetIndex < sheets.length) {
          const targetSheet = sheets[sheetIndex];
          if (targetSheet) {
            workbook.setActiveSheet(targetSheet);
            console.log(`📋 Activated sheet at index ${sheetIndex}: ${targetSheet.getName()}`);
          }
        } else {
          console.warn(`⚠️ Sheet index ${sheetIndex} out of range (0-${sheets.length - 1})`);
        }
      } catch (err) {
        console.error('❌ Error activating sheet:', err);
      }
    }
  }));

  useEffect(() => {
    if (!containerRef.current || !data || data.length === 0) {
      console.log('⚠️ Univer: Container or data not ready');
      return;
    }

    if (univerRef.current) {
      console.log('⚠️ Univer: Already initialized, skipping');
      return;
    }

    console.log('🚀 Initializing Univer with', data.length, 'sheets');
    console.log('📊 Raw data received:', data);

    const initUniver = async () => {
      try {
        // Create Univer instance (Preset Mode)
        // Note: In Preset Mode, createUniver returns { univer, univerAPI }
        const univerResult = createUniver(containerRef.current!);
        const univer = typeof univerResult === 'object' && 'univer' in univerResult 
          ? univerResult.univer 
          : univerResult;
        const univerAPI = typeof univerResult === 'object' && 'univerAPI' in univerResult 
          ? univerResult.univerAPI 
          : null;
        
        univerRef.current = univer;
        univerAPIRef.current = univerAPI;
        
        // Expose for debugging (already exposed in setup, but keep for compatibility)
        (window as any).__univer__ = univer;
        (window as any).__univerAPI__ = univerAPI;

        // Convert simple format to Univer workbook data
        const workbookData = convertToUniverFormat(data);
        console.log('📦 Converted workbook data');

        // Create workbook using Preset Mode API
        await createWorkbookWithData(workbookData);
        console.log('✅ Univer initialized successfully with Preset Mode ribbon');
        
        // Set up selection polling (using command execution tracking)
        if (univerAPI && onRangeSelect && univer) {
          try {
            console.log('🔧 Setting up selection tracking...');
            
            // Use the SelectionChanged event from Univer API
            if (univerAPI.Event?.SelectionChanged) {
              try {
                const disposable = univerAPI.addEvent(univerAPI.Event.SelectionChanged, (params: any) => {
                  const { worksheet, selections } = params;
                  
                  if (selections && selections.length > 0) {
                    const selection = selections[0];
                    const range = selection.range || selection;
                    
                    if (range) {
                      // Get sheet name safely
                      let sheetName = 'Sheet 1';
                      try {
                        if (worksheet && typeof worksheet.getName === 'function') {
                          sheetName = worksheet.getName();
                        } else if (worksheet && worksheet.name) {
                          sheetName = worksheet.name;
                        } else {
                          const activeSheet = univerAPI.getActiveWorkbook()?.getActiveSheet();
                          if (activeSheet && typeof activeSheet.getName === 'function') {
                            sheetName = activeSheet.getName();
                          }
                        }
                      } catch (e) {
                        // Use default sheet name
                      }
                      
                      const selectionInfo = {
                        sheet: sheetName,
                        startRow: range.startRow,
                        startCol: range.startColumn || range.startCol,
                        endRow: range.endRow,
                        endCol: range.endColumn || range.endCol
                      };
                      onRangeSelect(selectionInfo);
                    }
                  }
                });
                (univer as any)._selectionDisposable = disposable;
                console.log('✅ Selection event listener registered');
              } catch (e) {
                console.error('Failed to register selection listener:', e);
              }
            }
            
            // Fallback: Should not reach here if SelectionChanged event works
            if (!univerAPI.Event?.SelectionChanged) {
              console.warn('⚠️ SelectionChanged event not available, selection tracking disabled');
            }
          } catch (err) {
            console.error('❌ Could not set up selection tracking:', err);
          }
        }

        // Set up active sheet change tracking
        if (univerAPI && onSheetChange && univer) {
          try {
            console.log('🔧 Setting up active sheet tracking...');
            
            // Listen for ActiveSheetChanged event
            if (univerAPI.Event?.ActiveSheetChanged) {
              const sheetDisposable = univerAPI.addEvent(univerAPI.Event.ActiveSheetChanged, (params: any) => {
                console.log('📋 Active sheet changed:', params);
                const { worksheet } = params;
                
                if (worksheet && onSheetChange) {
                  // Get index
                  const workbook = univerAPI.getActiveWorkbook();
                  const sheets = workbook.getSheets();
                  const index = sheets.findIndex((s: any) => s.getId() === worksheet.getId());
                  
                  onSheetChange({
                    name: worksheet.getName(),
                    index: index >= 0 ? index : 0
                  });
                }
              });
              (univer as any)._sheetChangeDisposable = sheetDisposable;
              console.log('✅ Active sheet event listener registered');
            }
          } catch (err) {
            console.error('❌ Could not set up active sheet tracking:', err);
          }
        }
        
        // Set up cell edit tracking to sync manual edits to backend
        if (univerAPI && onCellEdit) {
          try {
            console.log('🔧 Setting up cell edit tracking...');
            
            // Listen for cell value changes using CellContentChanged event
            if (univerAPI.Event?.CellContentChanged) {
              const editDisposable = univerAPI.addEvent(univerAPI.Event.CellContentChanged, (params: any) => {
                console.log('📝 Cell content changed:', params);
                const { worksheet, row, column, value, oldValue } = params;
                
                // Get sheet name and index
                let sheetName = 'Sheet 1';
                let sheetIndex = 0;
                try {
                  if (worksheet && typeof worksheet.getName === 'function') {
                    sheetName = worksheet.getName();
                  }
                  const workbook = univerAPI.getActiveWorkbook();
                  if (workbook) {
                    const sheets = Object.keys(workbook.getSnapshot().sheets);
                    sheetIndex = sheets.findIndex((id: string) => {
                      const s = workbook.getSheetBySheetId(id);
                      return s && s.getName() === sheetName;
                    });
                    if (sheetIndex === -1) sheetIndex = 0;
                  }
                } catch (e) {
                  console.warn('Could not get sheet info:', e);
                }
                
                // Get formula if present
                let formula: string | undefined;
                try {
                  const range = worksheet.getRange(row, column, 1, 1);
                  formula = range.getFormula();
                } catch (e) { /* ignore */ }
                
                onCellEdit({
                  sheetName,
                  sheetIndex,
                  row,
                  col: column,
                  value: value?.v ?? value,
                  formula
                });
              });
              (univer as any)._cellEditDisposable = editDisposable;
              console.log('✅ Cell edit tracking registered');
            } else {
              console.warn('⚠️ CellContentChanged event not available, trying alternative...');
              
              // Try SheetValueChanged as alternative
              if (univerAPI.Event?.SheetValueChanged) {
                const editDisposable = univerAPI.addEvent(univerAPI.Event.SheetValueChanged, (params: any) => {
                  console.log('📝 Sheet value changed:', params);
                  // Handle the change
                  if (params.changes && Array.isArray(params.changes)) {
                    params.changes.forEach((change: any) => {
                      const workbook = univerAPI.getActiveWorkbook();
                      const activeSheet = workbook?.getActiveSheet();
                      const sheetName = activeSheet?.getName() || 'Sheet 1';
                      
                      onCellEdit({
                        sheetName,
                        sheetIndex: 0,
                        row: change.row,
                        col: change.column,
                        value: change.value?.v ?? change.value,
                        formula: change.value?.f
                      });
                    });
                  }
                });
                (univer as any)._cellEditDisposable = editDisposable;
                console.log('✅ Cell edit tracking registered (SheetValueChanged)');
              } else {
                console.warn('⚠️ No cell edit events available');
              }
            }
          } catch (err) {
            console.error('❌ Could not set up cell edit tracking:', err);
          }
        }
        
        // Add click event listener for debugging
        if (containerRef.current) {
          containerRef.current.addEventListener('click', (e) => {
            console.log('🖱️ Click detected on Univer container:', e.target);
          });
          containerRef.current.addEventListener('dblclick', (e) => {
            console.log('🖱️🖱️ Double-click detected on Univer container:', e.target);
          });
        }

      } catch (error) {
        console.error('❌ Error initializing Univer:', error);
        // Clean up on error
        if (univerRef.current) {
          try {
            univerRef.current.dispose();
          } catch (err) {
            console.error('Error disposing after init failure:', err);
          }
          univerRef.current = null;
        }
      }
    };
    
    initUniver();

    return () => {
      if (univerRef.current) {
        try {
          // Dispose selection event listener if it exists
          if ((univerRef.current as any)._selectionDisposable) {
            (univerRef.current as any)._selectionDisposable.dispose();
          }
          // Dispose active sheet event listener if it exists
          if ((univerRef.current as any)._sheetChangeDisposable) {
            (univerRef.current as any)._sheetChangeDisposable.dispose();
          }
          // Dispose cell edit listener if it exists
          if ((univerRef.current as any)._cellEditDisposable) {
            (univerRef.current as any)._cellEditDisposable.dispose();
          }
          univerRef.current.dispose();
        } catch (err) {
          console.error('Error disposing Univer:', err);
        }
        univerRef.current = null;
      }
    };
  }, [data, onRangeSelect, onCellEdit, onSheetChange]);

  return (
    <div 
      ref={containerRef} 
      className="univer-container"
      style={{ width: '100%', height: '100%', minHeight: 0 }}
    />
  );
});

/**
 * Apply a single modification to the Univer instance
 */
async function applyModification(univerAPI: any, mod: any): Promise<void> {
  console.log('  🔧 Applying modification:', mod.type, mod);

  const activeWorkbook = univerAPI.getActiveWorkbook();
  if (!activeWorkbook) {
    throw new Error('No active workbook');
  }

  // Get sheet by name or index
  let sheet: any;
  if (typeof mod.sheet === 'number') {
    const sheets = activeWorkbook.getSnapshot().sheets;
    const sheetId = Object.keys(sheets)[mod.sheet];
    sheet = activeWorkbook.getSheetBySheetId(sheetId);
  } else {
    sheet = activeWorkbook.getSheetByName(mod.sheet);
  }

  if (!sheet && mod.type !== 'add_sheet') {
    throw new Error(`Sheet not found: ${mod.sheet}`);
  }

  switch (mod.type) {
    case 'set_cell':
    case 'write_cell': {
      const range = sheet.getRange(mod.data.row, mod.data.col, 1, 1);
      const value = mod.data.value;
      // Check if value is a formula
      if (typeof value === 'string' && value.startsWith('=')) {
        range.setFormula(value);
        // Trigger formula calculation
        try {
          const formulaEngine = univerAPI.getFormula();
          if (formulaEngine) formulaEngine.executeCalculation();
        } catch (err) { /* ignore */ }
      } else {
        range.setValue(value);
      }
      break;
    }

    case 'batch_update': {
      console.log(`  📝 Applying batch update: ${mod.data.cellCount} cells`);
      let hasFormulas = false;
      mod.data.updates.forEach((update: any) => {
        const range = sheet.getRange(update.row, update.col, 1, 1);
        const value = update.value;
        // Check if value is a formula
        if (typeof value === 'string' && value.startsWith('=')) {
          range.setFormula(value);
          hasFormulas = true;
        } else {
          range.setValue(value);
        }
      });
      // Trigger formula calculation if any formulas were set
      if (hasFormulas) {
        try {
          const formulaEngine = univerAPI.getFormula();
          if (formulaEngine) {
            formulaEngine.executeCalculation();
            console.log('  🧮 Formula calculation triggered');
          }
        } catch (err) { /* ignore */ }
      }
      console.log(`  ✅ Batch update applied`);
      break;
    }

    case 'set_range':
    case 'write_range': {
      const { startRow, startCol, values } = mod.data;
      const rowCount = values.length;
      const colCount = values[0]?.length || 0;
      const range = sheet.getRange(startRow, startCol, rowCount, colCount);
      range.setValues(values);
      break;
    }

    case 'set_formula': {
      const range = sheet.getRange(mod.data.row, mod.data.col, 1, 1);
      // Add = prefix if missing
      const formula = mod.data.formula.startsWith('=') ? mod.data.formula : `=${mod.data.formula}`;
      range.setFormula(formula);
      
      // Trigger formula calculation
      try {
        const formulaEngine = univerAPI.getFormula();
        if (formulaEngine) {
          formulaEngine.executeCalculation();
          console.log('  🧮 Formula calculation triggered');
        }
      } catch (err) {
        console.log('  ⚠️ Could not trigger formula calculation:', err);
      }
      break;
    }

    case 'set_cell_font': {
      // Set font styling (bold, italic, color, size)
      const { startRow, startCol, endRow, endCol, fontStyle } = mod.data;
      const rowCount = endRow - startRow + 1;
      const colCount = endCol - startCol + 1;
      const range = sheet.getRange(startRow, startCol, rowCount, colCount);
      
      if (fontStyle.bold !== undefined) range.setFontWeight(fontStyle.bold ? 'bold' : 'normal');
      if (fontStyle.italic !== undefined) range.setFontStyle(fontStyle.italic ? 'italic' : 'normal');
      if (fontStyle.underline !== undefined) range.setUnderline(fontStyle.underline);
      if (fontStyle.color) range.setFontColor(fontStyle.color);
      if (fontStyle.size) range.setFontSize(fontStyle.size);
      if (fontStyle.family) range.setFontFamily(fontStyle.family);
      
      console.log('  ✅ Applied font styling');
      break;
    }

    case 'set_cell_background': {
      // Set background color
      const { startRow, startCol, endRow, endCol, color } = mod.data;
      const rowCount = endRow - startRow + 1;
      const colCount = endCol - startCol + 1;
      const range = sheet.getRange(startRow, startCol, rowCount, colCount);
      range.setBackground(`#${color}`);
      console.log('  ✅ Applied background color:', color);
      break;
    }

    case 'format_cells': {
      // Apply cell formatting (colors, fonts, alignment)
      const { startRow, startCol, endRow, endCol, format } = mod.data;
      const rowCount = endRow - startRow + 1;
      const colCount = endCol - startCol + 1;
      const range = sheet.getRange(startRow, startCol, rowCount, colCount);

      // Apply formatting
      if (format.bold !== undefined) range.setFontWeight(format.bold ? 'bold' : 'normal');
      if (format.italic !== undefined) range.setFontStyle(format.italic ? 'italic' : 'normal');
      if (format.underline !== undefined) range.setUnderline(format.underline);
      if (format.fontSize) range.setFontSize(format.fontSize);
      if (format.fontColor) range.setFontColor(format.fontColor);
      if (format.backgroundColor) range.setBackground(format.backgroundColor);
      if (format.horizontalAlign) range.setHorizontalAlignment(format.horizontalAlign);
      if (format.verticalAlign) range.setVerticalAlignment(format.verticalAlign);
      
      console.log('  ✅ Applied formatting:', format);
      break;
    }

    case 'format_numbers': {
      // Apply number formatting
      const { startRow, startCol, endRow, endCol, formatCode } = mod.data;
      const rowCount = endRow - startRow + 1;
      const colCount = endCol - startCol + 1;
      const range = sheet.getRange(startRow, startCol, rowCount, colCount);
      range.setNumberFormat(formatCode);
      break;
    }

    case 'clear_cells': {
      // Clear cell content
      const { startRow, startCol, endRow, endCol } = mod.data;
      const rowCount = endRow - startRow + 1;
      const colCount = endCol - startCol + 1;
      const range = sheet.getRange(startRow, startCol, rowCount, colCount);
      range.clear();
      break;
    }

    case 'insert_rows': {
      // Insert rows (requires full reload)
      console.warn('  ⚠️ insert_rows requires full reload');
      break;
    }

    case 'delete_rows': {
      // Delete rows (requires full reload)
      console.warn('  ⚠️ delete_rows requires full reload');
      break;
    }

    case 'add_sheet':
    case 'rename_sheet':
    case 'delete_sheet': {
      // Sheet operations (requires full reload)
      console.warn(`  ⚠️ ${mod.type} requires full reload`);
      break;
    }

    case 'set_number_format': {
      const { startRow, startCol, endRow, endCol, format } = mod.data;
      const rowCount = endRow - startRow + 1;
      const colCount = endCol - startCol + 1;
      const range = sheet.getRange(startRow, startCol, rowCount, colCount);
      range.setNumberFormat(format);
      console.log('  ✅ Applied number format:', format);
      break;
    }

    case 'set_alignment': {
      const { startRow, startCol, endRow, endCol, horizontal, vertical } = mod.data;
      const rowCount = endRow - startRow + 1;
      const colCount = endCol - startCol + 1;
      const range = sheet.getRange(startRow, startCol, rowCount, colCount);
      if (horizontal) range.setHorizontalAlignment(horizontal);
      if (vertical) range.setVerticalAlignment(vertical);
      console.log('  ✅ Applied alignment - h:', horizontal, 'v:', vertical);
      break;
    }

    case 'set_border': {
      const { startRow, startCol, endRow, endCol, top, bottom, left, right, style, color } = mod.data;
      const rowCount = endRow - startRow + 1;
      const colCount = endCol - startCol + 1;
      const range = sheet.getRange(startRow, startCol, rowCount, colCount);
      
      // Convert style to Univer border weight
      const borderStyle = style === 'thick' ? 3 : style === 'medium' ? 2 : 1;
      const borderColor = color ? `#${color}` : '#000000';
      
      // Set borders using Univer's border API
      if (top || bottom || left || right) {
        range.setBorder(top, left, bottom, right, false, false, borderColor, borderStyle);
      }
      console.log('  ✅ Applied borders');
      break;
    }

    case 'set_text_wrap': {
      const { startRow, startCol, endRow, endCol, wrap } = mod.data;
      const rowCount = endRow - startRow + 1;
      const colCount = endCol - startCol + 1;
      const range = sheet.getRange(startRow, startCol, rowCount, colCount);
      range.setWrap(wrap);
      console.log('  ✅ Set text wrap:', wrap);
      break;
    }

    case 'set_freeze_rows': {
      const { numRows } = mod.data;
      try {
        // Using Univer's freeze API
        const activeSheet = univerAPI.getActiveWorkbook()?.getActiveSheet();
        if (activeSheet) {
          activeSheet.setFreeze(numRows, 0);
          console.log('  ✅ Froze', numRows, 'rows');
        }
      } catch (err) {
        console.warn('  ⚠️ Could not freeze rows:', err);
      }
      break;
    }

    case 'set_freeze_columns': {
      const { numColumns } = mod.data;
      try {
        const activeSheet = univerAPI.getActiveWorkbook()?.getActiveSheet();
        if (activeSheet) {
          activeSheet.setFreeze(0, numColumns);
          console.log('  ✅ Froze', numColumns, 'columns');
        }
      } catch (err) {
        console.warn('  ⚠️ Could not freeze columns:', err);
      }
      break;
    }

    case 'set_note': {
      const { row, col, note } = mod.data;
      try {
        const range = sheet.getRange(row, col, 1, 1);
        if (note) {
          // Use thread comment API - create rich text and comment builder
          const richText = univerAPI.newRichText().insertText(note);
          const commentBuilder = univerAPI.newTheadComment().setContent(richText);
          await range.addCommentAsync(commentBuilder);
          console.log('  ✅ Added comment to cell');
        } else {
          // Clear existing comment
          await range.clearCommentAsync();
          console.log('  ✅ Cleared comment from cell');
        }
      } catch (err) {
        console.warn('  ⚠️ Could not set comment:', err);
      }
      break;
    }

    case 'copy_range': {
      // Copy already happened on backend, just log
      console.log('  ✅ Range copied on backend');
      break;
    }

    case 'set_column_width': {
      const { startCol, endCol, width } = mod.data;
      try {
        for (let col = startCol; col <= (endCol ?? startCol); col++) {
          // Univer uses pixels, Excel uses character widths. Approx conversion: 1 Excel unit ≈ 7 pixels
          const pixelWidth = width * 7;
          sheet.setColumnWidth(col, pixelWidth);
        }
        console.log('  ✅ Set column width:', width, 'for cols', startCol, 'to', endCol ?? startCol);
      } catch (err) {
        console.warn('  ⚠️ Could not set column width:', err);
      }
      break;
    }

    case 'set_row_height': {
      const { startRow, endRow, height } = mod.data;
      try {
        for (let row = startRow; row <= (endRow ?? startRow); row++) {
          sheet.setRowHeight(row, height);
        }
        console.log('  ✅ Set row height:', height, 'for rows', startRow, 'to', endRow ?? startRow);
      } catch (err) {
        console.warn('  ⚠️ Could not set row height:', err);
      }
      break;
    }

    default:
      console.warn('  ⚠️ Unknown modification type:', mod.type);
  }
}

/**
 * Convert simple 2D array format to Univer workbook format
 */
function convertToUniverFormat(sheets: SheetData[]) {
  const workbookSheets: Record<string, any> = {};
  const sheetOrder: string[] = [];

  sheets.forEach((sheet, index) => {
    const sheetId = `sheet${index}`;
    sheetOrder.push(sheetId);

    console.log(`  📋 Converting sheet:`, sheet);

    // Ensure rows exists
    const rows = sheet.rows || [];
    console.log(`  📋 Sheet "${sheet.name}": ${rows.length} rows`);

    // Convert 2D array to Univer cell data format
    const cellData: Record<number, Record<number, any>> = {};

    rows.forEach((row, rowIndex) => {
      // Handle both array rows and object rows (from batch_update_cells)
      if (Array.isArray(row)) {
        // Array format - iterate with forEach
        row.forEach((cellData_raw, colIndex) => {
          processCellData(cellData_raw, rowIndex, colIndex, cellData);
        });
      } else if (row && typeof row === 'object') {
        // Object format with numeric keys - iterate over keys
        Object.keys(row).forEach(colKey => {
          const colIndex = parseInt(colKey);
          const cellData_raw = row[colKey];
          processCellData(cellData_raw, rowIndex, colIndex, cellData);
        });
      }
    });
    
    // Helper function to process individual cells
    function processCellData(cellData_raw: any, rowIndex: number, colIndex: number, cellData: Record<number, Record<number, any>>) {
      let univerCell: any = null;
      
      if (cellData_raw && typeof cellData_raw === 'object') {
        // Rich cell object from backend - convert to Univer format
        // NOTE: Cell might have style but no value (formatted empty cell)
        univerCell = {
          v: cellData_raw.v !== undefined ? cellData_raw.v : '',
        };
        
        // Add type if present (convert XLSX type to Univer CellValueType)
        if (cellData_raw.t) {
          univerCell.t = xlsxTypeToUniver(cellData_raw.t);
        }
        
        // Add formula if present - keep the = prefix
        if (cellData_raw.f) {
          const formula = cellData_raw.f.startsWith('=') ? cellData_raw.f : `=${cellData_raw.f}`;
          univerCell.f = formula;
          univerCell.v = null; // Clear value so formula recalculates
          if (rowIndex === 0 && colIndex === 0) {
            console.log(`📊 Formula detected (example): "${cellData_raw.f}" → "${formula}"`);
          }
        }
        
        // 🎨 Convert XLSX style to Univer style format
        if (cellData_raw.s) {
          const convertedStyle = convertXLSXStyleToUniver(cellData_raw.s);
          univerCell.s = convertedStyle;
          // Style conversion logging removed for performance
        }
      } else if (cellData_raw !== null && cellData_raw !== undefined && cellData_raw !== '') {
        // Simple value (fallback)
        univerCell = {
          v: cellData_raw,
          t: inferCellType(cellData_raw),
        };
      }
      
      if (univerCell) {
        if (!cellData[rowIndex]) {
          cellData[rowIndex] = {};
        }
        cellData[rowIndex][colIndex] = univerCell;
      }
    }

    // Count cells for summary
    let cellCount = 0;
    let styledCount = 0;
    Object.keys(cellData).forEach(rowKey => {
      const row = cellData[parseInt(rowKey)];
      const colKeys = Object.keys(row);
      cellCount += colKeys.length;
      colKeys.forEach(colKey => {
        if (row[parseInt(colKey)]?.s) {
          styledCount++;
        }
      });
    });
    
    console.log(`  ✅ Converted ${cellCount} cells (${styledCount} with styles)`);

    // 📏 Build column data with widths
    const columnData: Record<number, any> = {};
    if (sheet.colWidths && sheet.colWidths.length > 0) {
      sheet.colWidths.forEach((width, colIndex) => {
        // Univer expects width in pixels
        columnData[colIndex] = {
          w: width,
          hd: 0, // hidden: 0 = visible
        };
      });
      console.log(`  📏 Applied ${sheet.colWidths.length} column widths (pixels)`, sheet.colWidths.slice(0, 5));
    } else {
      console.warn(`  ⚠️ No column widths found in sheet data`);
    }
    
    // 📏 Build row data with heights
    const rowData: Record<number, any> = {};
    if (sheet.rowHeights && sheet.rowHeights.length > 0) {
      sheet.rowHeights.forEach((height, rowIndex) => {
        rowData[rowIndex] = {
          h: height,
          hd: 0, // hidden: 0 = visible
        };
      });
      console.log(`  📏 Applied ${sheet.rowHeights.length} row heights`);
    }
    
    // 🔗 Convert merged cells to Univer format
    const mergeData = (sheet.merges || []).map((merge: any) => {
      const univerMerge = {
        startRow: merge.s.r,
        endRow: merge.e.r,
        startColumn: merge.s.c,
        endColumn: merge.e.c,
      };
      console.log(`    🔗 Merge: R${merge.s.r+1}C${merge.s.c+1}:R${merge.e.r+1}C${merge.e.c+1} → Univer:`, univerMerge);
      return univerMerge;
    });
    if (mergeData.length > 0) {
      console.log(`  🔗 Applied ${mergeData.length} merged cell ranges`);
    }

    workbookSheets[sheetId] = {
      id: sheetId,
      name: sheet.name,
      cellData,
      rowCount: sheet.rowCount,
      columnCount: sheet.columnCount,
      defaultColumnWidth: sheet.defaultColWidth || 73,
      defaultRowHeight: sheet.defaultRowHeight || 23,
      zoomRatio: 1,
      scrollTop: 0,
      scrollLeft: 0,
      freeze: {
        startRow: -1,
        startColumn: -1,
        ySplit: 0,
        xSplit: 0,
      },
      mergeData,
      rowData,
      columnData,
      rowHeader: {
        width: 46,
        hidden: 0,
      },
      columnHeader: {
        height: 20,
        hidden: 0,
      },
      showGridlines: 1,
      hidden: 0,
      tabColor: '',
      rightToLeft: 0,
      // Explicitly mark sheet as editable and unprotected
      status: 1, // 1 = normal/editable, 0 = hidden
      protection: undefined, // No sheet protection
    };
    
    console.log(`  ✅ Sheet "${sheet.name}" configured - ${sheet.rows.length} rows`);
  });

  const result = {
    id: 'workbook',
    name: 'Workbook',
    appVersion: '0.1.0',
    locale: 'en-US',
    sheets: workbookSheets,
    sheetOrder,
    // Explicitly configure for editing
    editable: true,
    // No workbook-level protection
    protection: undefined,
  };
  
  console.log('📦 Final workbook structure (editable):', JSON.stringify(result, null, 2).substring(0, 2000));
  return result;
}

/**
 * Convert XLSX style format to Univer style format
 */
function convertXLSXStyleToUniver(xlsxStyle: any): any {
  if (!xlsxStyle) return {};
  
  const univerStyle: any = {};
  
  // 🔤 Font styling
  if (xlsxStyle.font) {
    if (xlsxStyle.font.bold) univerStyle.bl = 1; // bold: 1 = true
    if (xlsxStyle.font.italic) univerStyle.it = 1; // italic: 1 = true
    if (xlsxStyle.font.underline) univerStyle.ul = { s: 1 }; // underline: single
    if (xlsxStyle.font.sz) univerStyle.fs = xlsxStyle.font.sz; // font size
    if (xlsxStyle.font.name) univerStyle.ff = xlsxStyle.font.name; // font family
    
    // Font color: XLSX uses { rgb: "RRGGBB" }, Univer uses { rgb: "#RRGGBB" }
    if (xlsxStyle.font.color?.rgb) {
      univerStyle.cl = { rgb: `#${xlsxStyle.font.color.rgb}` };
    }
  }
  
  // 🎨 Background color
  // XLSX cell styles can have fill info in two places:
  // 1. Nested: xlsxStyle.fill.fgColor.rgb
  // 2. Direct: xlsxStyle.fgColor.rgb (when cellStyles: true is used)
  
  let fgColor = null;
  let patternType = null;
  
  // Check for nested fill object first
  if (xlsxStyle.fill?.fgColor) {
    fgColor = xlsxStyle.fill.fgColor;
    patternType = xlsxStyle.fill.patternType;
  }
  // Otherwise check if fill properties are directly on the style object
  else if (xlsxStyle.fgColor) {
    fgColor = xlsxStyle.fgColor;
    patternType = xlsxStyle.patternType;
  }
  
  // Apply background color if we have fgColor and it's a solid pattern
  if (fgColor && patternType && patternType !== 'none') {
    // Priority: rgb > theme (with tint) > indexed
    if (fgColor.rgb) {
      // Remove any leading # if present, then add it back
      const rgb = fgColor.rgb.replace(/^#/, '');
      univerStyle.bg = { rgb: `#${rgb}` };
    } else if (fgColor.theme !== undefined && fgColor.tint !== undefined) {
      // For theme colors, XLSX library should have already resolved to RGB
      console.warn('⚠️ Theme color detected but no RGB provided:', fgColor);
    } else if (fgColor.indexed !== undefined) {
      console.warn('⚠️ Indexed color detected, needs color palette lookup:', fgColor);
    }
  }
  
  // 📐 Alignment
  if (xlsxStyle.alignment) {
    // Horizontal: left=1, center=2, right=3
    if (xlsxStyle.alignment.horizontal) {
      const hAlignMap: Record<string, number> = {
        'left': 1,
        'center': 2,
        'right': 3,
      };
      univerStyle.ht = hAlignMap[xlsxStyle.alignment.horizontal] || 1;
    }
    
    // Vertical: top=1, middle=2, bottom=3
    if (xlsxStyle.alignment.vertical) {
      const vAlignMap: Record<string, number> = {
        'top': 1,
        'middle': 2,
        'bottom': 3,
      };
      univerStyle.vt = vAlignMap[xlsxStyle.alignment.vertical] || 2;
    }
    
    // Text wrap
    if (xlsxStyle.alignment.wrapText) {
      univerStyle.tb = 3; // 1=clip, 2=overflow, 3=wrap
    }
  }
  
  // 🔲 Borders
  if (xlsxStyle.border) {
    univerStyle.bd = {};
    
    const borderStyleMap: Record<string, number> = {
      'thin': 1,
      'medium': 2,
      'thick': 3,
      'double': 3,
      'hair': 1,
      'dotted': 1,
      'dashed': 1,
    };
    
    if (xlsxStyle.border.top) {
      const borderStyle = borderStyleMap[xlsxStyle.border.top.style] || 1;
      const borderColor = xlsxStyle.border.top.color?.rgb || '000000';
      univerStyle.bd.t = { s: borderStyle, cl: { rgb: '#' + borderColor } };
    }
    if (xlsxStyle.border.bottom) {
      const borderStyle = borderStyleMap[xlsxStyle.border.bottom.style] || 1;
      const borderColor = xlsxStyle.border.bottom.color?.rgb || '000000';
      univerStyle.bd.b = { s: borderStyle, cl: { rgb: '#' + borderColor } };
    }
    if (xlsxStyle.border.left) {
      const borderStyle = borderStyleMap[xlsxStyle.border.left.style] || 1;
      const borderColor = xlsxStyle.border.left.color?.rgb || '000000';
      univerStyle.bd.l = { s: borderStyle, cl: { rgb: '#' + borderColor } };
    }
    if (xlsxStyle.border.right) {
      const borderStyle = borderStyleMap[xlsxStyle.border.right.style] || 1;
      const borderColor = xlsxStyle.border.right.color?.rgb || '000000';
      univerStyle.bd.r = { s: borderStyle, cl: { rgb: '#' + borderColor } };
    }
  }
  
  return univerStyle;
}

/**
 * Convert XLSX cell type to Univer cell type
 */
function xlsxTypeToUniver(xlsxType: string): number {
  // XLSX types: s=string, n=number, b=boolean, e=error, d=date
  // Univer types: 1=string, 2=number, 3=boolean, 4=force text
  switch (xlsxType) {
    case 'n': return 2; // number
    case 'd': return 2; // date (stored as number in Excel)
    case 'b': return 3; // boolean
    case 's':
    default:
      return 1; // string
  }
}

/**
 * Infer Univer cell type from value (fallback for simple values)
 */
function inferCellType(value: any): number {
  // Univer CellValueType: 1=string, 2=number, 3=boolean
  if (typeof value === 'number') return 2;
  if (typeof value === 'boolean') return 3;
  return 1; // string
}
